import styles from './style.scss';

import {html} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import type {Placement} from '@floating-ui/dom';
import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
} from '@floating-ui/dom';

import {FlywheelComponent} from '@gl/lit/lib/component.js';
import {ImmutableComponent} from '@gl/lit/components/mixins/immutable_component/index.js';
import type {Constructor} from '@gl/lit/types/classes.js';
import {
  isDeviceHighPixelRatio,
  roundByDevicePixelRatio,
} from '@gl/lit/utils/dom.js';
import {isNull, isString} from '@gl/lit/utils/index.js';
import {ClickOutsideController} from '@gl/lit/controllers/dom_behaviour/click_outside.controller.js';
import type {Nullable} from '@gl/lit/types/general.js';
import type {Model} from './architecture.js';
import {Msg, main, DEFAULT_PLACEMENT} from './architecture.js';

export enum DropdownPlacement {
  BOTTOM = 'bottom',
  BOTTOM_START = 'bottom-start',
  BOTTOM_END = 'bottom-end',
  TOP = 'top',
  TOP_START = 'top-start',
  TOP_END = 'top-end',
  RIGHT = 'right',
  RIGHT_START = 'right-start',
  RIGHT_END = 'right-end',
  LEFT = 'left',
  LEFT_START = 'left-start',
  LEFT_END = 'left-end',
}

export type OpenEvent = {
  detail: {visible: true};
};

export type CloseEvent = {
  detail: {visible: false};
};

export type EventMap = {
  open: OpenEvent;
  close: CloseEvent;
};

const COMPONENT_TAG = 'fly-floating-surface-v2';
const SURFACE_GAP_PX = 8;

export const DROPDOWN_PLACEMENT = new Set(Object.values(DropdownPlacement));

function isValidPlacement(value: unknown): value is Placement {
  return isString(value) && DROPDOWN_PLACEMENT.has(value as DropdownPlacement);
}

@customElement(COMPONENT_TAG)
export default class FlyFloatingSurfaceV2 extends ImmutableComponent<
  Constructor<FlywheelComponent>,
  Model,
  Msg
>({component: FlywheelComponent, main}) {
  static get styles() {
    return [styles];
  }

  clickOutsideController = new ClickOutsideController(
    this,
    this.model.hideOnClickOutside
  );

  /**
   * this tells the floating ui lib to recalculate the position every animation frame
   * (use with moderation as this is an expensive call)
   * default is to listen to scroll events when set to false
   */
  @property({type: Boolean})
  updateOnAnimationFrame = false;

  private unlistenAnchorPosition: VoidFunction = () => {};

  @property({type: Boolean})
  get visible() {
    return this.model.visible;
  }
  set visible(value: boolean) {
    const oldValue = this.visible;

    if (value && !this.anchor) {
      console.error("Surface can't be visible without an anchor");
      return;
    }

    if (value !== oldValue) {
      this.handleUpdate(value ? Msg.show() : Msg.hide());
      this.requestUpdate('visible', value);
    }
  }

  @property({
    type: String,
    reflect: true,
    converter(value: unknown): Placement {
      return isValidPlacement(value) ? value : DEFAULT_PLACEMENT;
    },
  })
  get placement(): Placement {
    return this.model.placement;
  }
  set placement(value: Placement) {
    const oldValue = this.placement;
    if (value !== oldValue) {
      this.handleUpdate(Msg.setPlacement(value));
      this.requestUpdate('placement', oldValue);
    }
  }

  @property({type: Boolean, reflect: true})
  get closeWhenClickOutside(): boolean {
    return this.model.hideOnClickOutside;
  }
  set closeWhenClickOutside(value: boolean) {
    const oldValue = this.closeWhenClickOutside;
    if (value !== oldValue) {
      this.handleUpdate(Msg.setClickOutsideBehaviour(value));
      this.requestUpdate('closeWhenClickOutside', oldValue);
    }
  }

  @property({attribute: false})
  get anchor(): Nullable<Element> {
    return this.model.anchorElement;
  }
  set anchor(value: Nullable<Element>) {
    const oldValue = this.anchor;
    if (value !== oldValue) {
      this.handleUpdate(
        isNull(value) ? Msg.unregisterAnchor() : Msg.registerAnchor(value)
      );
      this.requestUpdate('anchor', oldValue);
    }
  }

  private emitEvent<EventName extends keyof EventMap>(
    eventName: EventName,
    detail: EventMap[EventName]['detail'],
    options?: EventInit
  ): void {
    const event = new CustomEvent(eventName, {
      detail,
      bubbles: true,
      cancelable: true,
      composed: true,
      ...options,
    });
    this.dispatchEvent(event);
  }

  private updateCSSTransform(x: number, y: number): void {
    const roundedX = roundByDevicePixelRatio(x);
    const roundedY = roundByDevicePixelRatio(y);

    this.style.transform = isDeviceHighPixelRatio()
      ? `translate3d(${roundedX}px,${roundedY}px,0)`
      : `translate(${roundedX}px,${roundedY}px)`;
  }

  private async computePosition() {
    if (this.anchor && this.visible) {
      const position = await computePosition(this.anchor, this, {
        placement: this.placement,
        strategy: 'fixed',
        middleware: [offset(SURFACE_GAP_PX), flip(), shift({padding: 10})],
      });
      this.updateCSSTransform(position.x, position.y);
    }
  }

  registerAnchorInController(element: Nullable<Element>) {
    element && this.clickOutsideController.setElementAsInside(element);
  }

  removeAnchorInController(element: Nullable<Element>) {
    element && this.clickOutsideController.setElementAsOutside(element);
  }

  updateClickOutsideBehaviour(shouldActivate: boolean) {
    shouldActivate
      ? this.clickOutsideController.activate()
      : this.clickOutsideController.deactivate();
  }

  handleAnchorPosition() {
    this.unlistenAnchorPosition();

    if (this.anchor && this.visible) {
      this.unlistenAnchorPosition = autoUpdate(
        this.anchor,
        this,
        () => this.computePosition(),
        {animationFrame: this.updateOnAnimationFrame}
      );
    }
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.clickOutsideController.subscribe(() => {
      this.visible = false;
    });
  }

  onShow() {
    this.emitEvent('open', {visible: true});
  }

  onHide() {
    this.emitEvent('close', {visible: false});
    this.removeAttribute('visible');
  }

  render() {
    return html`<slot></slot>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [COMPONENT_TAG]: FlyFloatingSurfaceV2;
  }
}
